import React from 'react';
import { Box, Typography, Button, Container, Paper, Chip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import ConstructionIcon from '@mui/icons-material/Construction';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const ComingSoon = ({ title, description }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Extract page name from pathname or use provided title
  const pageName = title || location.pathname.split('/').pop().replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
        p: 2
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={8}
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              borderRadius: 4,
              backgroundColor: 'background.paper'
            }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <ConstructionIcon
                sx={{
                  fontSize: { xs: 80, md: 120 },
                  color: 'warning.main',
                  mb: 2
                }}
              />
            </motion.div>

            <Chip
              icon={<AccessTimeIcon />}
              label="Coming Soon"
              color="warning"
              sx={{
                mb: 3,
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            />

            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 'bold',
                color: 'primary.main',
                mb: 2
              }}
            >
              {pageName}
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                fontWeight: 600,
                color: 'text.primary',
                mb: 2
              }}
            >
              Under Development
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                mb: 4,
                fontSize: { xs: '1rem', md: '1.125rem' },
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              {description || 
                `We're working hard to bring you this feature. The ${pageName} page is currently under development and will be available soon. Thank you for your patience!`
              }
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'center',
                mt: 4
              }}
            >
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                sx={{
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#FF735C',
                  '&:hover': {
                    backgroundColor: '#ff5a3c'
                  }
                }}
              >
                Go to Dashboard
              </Button>

              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
                sx={{
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.light'
                  }
                }}
              >
                Go Back
              </Button>
            </Box>

            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem'
                }}
              >
                Expected completion: Q2 2024 • For updates, contact your system administrator
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ComingSoon;